import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/reducers';
import { getSumOpenInstallments, getSumOpenInstallmentsDetailed } from '../../../store/actions/installmentActions';
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Grid,
    Card,
    CardContent
} from '@mui/material';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    BarChart,
    Bar,
    Cell
} from 'recharts';
import { format, parseISO, isValid, subMonths } from 'date-fns';

const CHART_COLORS = ['#1976d2', '#dc004e', '#ff9800', '#4caf50', '#9c27b0'];

const formatBRL = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const Dashboard2: React.FC = () => {
    const dispatch = useDispatch();

    const sumOpen = useSelector((state: RootState) => state.installments.sumOpen);
    const sumOpenDetailed = useSelector((state: RootState) => state.installments.sumOpenDetailed);
    const isFetchingSummary = useSelector((state: RootState) => state.installments.isFetchingSummary);
    const isFetchingDetailed = useSelector((state: RootState) => state.installments.isFetchingDetailed);

    useEffect(() => {
        // @ts-ignore
        dispatch(getSumOpenInstallments());
        // @ts-ignore
        dispatch(getSumOpenInstallmentsDetailed());
    }, [dispatch]);

    // Monthly revenue trend (last 12 months) based on paid installments
    const revenueTrendData = useMemo(() => {
        if (!Array.isArray(sumOpenDetailed)) return [];

        const now = new Date();
        const twelveMonthsAgo = subMonths(now, 12);
        const byMonth: Record<string, { month: string; Receita: number }> = {};

        // Initialize last 12 months
        for (let i = 11; i >= 0; i--) {
            const d = subMonths(now, i);
            const key = format(d, 'yyyy-MM');
            const label = format(d, 'MM/yyyy');
            byMonth[key] = { month: label, Receita: 0 };
        }

        sumOpenDetailed.forEach((item: any) => {
            if (!item.DataPagto) return;
            const payDate = parseISO(item.DataPagto);
            if (!isValid(payDate)) return;
            if (payDate < twelveMonthsAgo || payDate > now) return;

            const key = format(payDate, 'yyyy-MM');
            if (byMonth[key]) {
                byMonth[key].Receita += item.AmountPaid || 0;
            }
        });

        return Object.entries(byMonth)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([, v]) => v);
    }, [sumOpenDetailed]);

    // Payment method breakdown
    const paymentMethodData = useMemo(() => {
        if (!Array.isArray(sumOpenDetailed)) return [];

        const methods: Record<string, number> = {
            'Dinheiro': 0,
            'Cartão Crédito': 0,
            'Cartão Débito': 0,
            'Cheque': 0,
            'PIX': 0,
        };

        const methodMap: Record<number, string> = {
            1: 'Dinheiro',
            2: 'Cartão Crédito',
            3: 'Cartão Débito',
            4: 'Cheque',
            5: 'PIX',
        };

        sumOpenDetailed.forEach((item: any) => {
            if (!item.AmountPaid || item.AmountPaid <= 0) return;
            const methodName = methodMap[item.PaymentType] || item.paymentTypeName || 'Outros';
            if (methods[methodName] !== undefined) {
                methods[methodName] += item.AmountPaid;
            }
        });

        return Object.entries(methods)
            .filter(([, val]) => val > 0)
            .map(([name, value]) => ({ name, value }));
    }, [sumOpenDetailed]);

    // Summary calculations
    const totalReceived = useMemo(() => {
        if (!Array.isArray(sumOpenDetailed)) return 0;
        return sumOpenDetailed.reduce((acc: number, item: any) => acc + (item.AmountPaid || 0), 0);
    }, [sumOpenDetailed]);

    const totalPending = useMemo(() => {
        if (!Array.isArray(sumOpenDetailed)) return 0;
        return sumOpenDetailed.reduce((acc: number, item: any) => {
            const saldo = (item.Amount || 0) - (item.AmountPaid || 0);
            return acc + Math.max(saldo, 0);
        }, 0);
    }, [sumOpenDetailed]);

    const totalInstallments = Array.isArray(sumOpenDetailed) ? sumOpenDetailed.length : 0;

    const averageTicket = useMemo(() => {
        const paidItems = Array.isArray(sumOpenDetailed)
            ? sumOpenDetailed.filter((i: any) => i.AmountPaid && i.AmountPaid > 0)
            : [];
        if (paidItems.length === 0) return 0;
        const sum = paidItems.reduce((acc: number, i: any) => acc + (i.AmountPaid || 0), 0);
        return sum / paidItems.length;
    }, [sumOpenDetailed]);

    const isLoading = isFetchingSummary || isFetchingDetailed;

    const tooltipFormatter = (value: number) => formatBRL(value);

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">Dashboard Financeiro 2</Typography>
                <Box />
            </Box>

            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {/* Summary Cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={3}>
                            <Card elevation={2}>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">Total Recebido</Typography>
                                    <Typography variant="h5" fontWeight="bold" color="success.main">
                                        {formatBRL(totalReceived)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <Card elevation={2}>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">Total Pendente</Typography>
                                    <Typography variant="h5" fontWeight="bold" color="error">
                                        {formatBRL(totalPending)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <Card elevation={2}>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">Total de Parcelas</Typography>
                                    <Typography variant="h5" fontWeight="bold" color="primary">
                                        {totalInstallments}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <Card elevation={2}>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">Ticket Médio</Typography>
                                    <Typography variant="h5" fontWeight="bold" color="secondary">
                                        {formatBRL(averageTicket)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Charts */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        {/* Chart 1: Monthly Revenue Trend */}
                        <Grid item xs={12} md={7}>
                            <Paper elevation={2} sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom>Tendência de Receita Mensal (últimos 12 meses)</Typography>
                                {revenueTrendData.length > 0 ? (
                                    <ResponsiveContainer width="100%" height={350}>
                                        {/* @ts-ignore - recharts types incompatible with React 18 */}
                                        <LineChart data={revenueTrendData}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="month" fontSize={11} angle={-30} textAnchor="end" height={50} />
                                            <YAxis
                                                tickFormatter={(v: number) => `R$${(v / 1000).toFixed(0)}k`}
                                                fontSize={12}
                                            />
                                            <Tooltip formatter={tooltipFormatter} />
                                            <Legend />
                                            <Line
                                                type="monotone"
                                                dataKey="Receita"
                                                stroke={CHART_COLORS[3]}
                                                strokeWidth={3}
                                                name="Receita"
                                                dot={{ fill: CHART_COLORS[3], r: 4 }}
                                                activeDot={{ r: 6 }}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 350 }}>
                                        <Typography color="text.secondary">Sem dados de receita disponíveis</Typography>
                                    </Box>
                                )}
                            </Paper>
                        </Grid>

                        {/* Chart 2: Payment Method Breakdown */}
                        <Grid item xs={12} md={5}>
                            <Paper elevation={2} sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom>Receita por Forma de Pagamento</Typography>
                                {paymentMethodData.length > 0 ? (
                                    <ResponsiveContainer width="100%" height={350}>
                                        {/* @ts-ignore - recharts types incompatible with React 18 */}
                                        <BarChart data={paymentMethodData} layout="vertical">
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis
                                                type="number"
                                                tickFormatter={(v: number) => `R$${(v / 1000).toFixed(0)}k`}
                                                fontSize={12}
                                            />
                                            <YAxis type="category" dataKey="name" width={110} fontSize={12} />
                                            <Tooltip formatter={tooltipFormatter} />
                                            <Bar dataKey="value" name="Valor" radius={[0, 4, 4, 0]}>
                                                {paymentMethodData.map((_entry, index) => {
                                                    const colors = [CHART_COLORS[3], CHART_COLORS[0], CHART_COLORS[0], CHART_COLORS[2], CHART_COLORS[4]];
                                                    return (
                                                        <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                                                    );
                                                })}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 350 }}>
                                        <Typography color="text.secondary">Sem dados de pagamento</Typography>
                                    </Box>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Revenue vs Pending comparison */}
                    <Paper elevation={2} sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>Resumo Geral</Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <Box sx={{
                                    p: 2,
                                    bgcolor: '#e8f5e9',
                                    borderRadius: 2,
                                    borderLeft: `4px solid ${CHART_COLORS[3]}`
                                }}>
                                    <Typography variant="body2" color="text.secondary">Receita Total Recebida</Typography>
                                    <Typography variant="h4" fontWeight="bold" color="success.main">
                                        {formatBRL(totalReceived)}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Box sx={{
                                    p: 2,
                                    bgcolor: '#fce4ec',
                                    borderRadius: 2,
                                    borderLeft: `4px solid ${CHART_COLORS[1]}`
                                }}>
                                    <Typography variant="body2" color="text.secondary">Total em Aberto</Typography>
                                    <Typography variant="h4" fontWeight="bold" color="error">
                                        {formatBRL(totalPending)}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                </>
            )}
        </Box>
    );
};

export default Dashboard2;
